import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import ActionMenu from "@/components/ActionMenu";
import GenericAlertModal from "@/components/AlertModal";

import useDebounce from "@/hooks/useDebounce";
import { usePageStore } from "@/hooks/usePageStore";
import { useUserStore } from "@/hooks/useUserStore";
import {
  useAuditLogFirstRender,
  useCreateAuditlog,
} from "@/hooks/useAuditLogs";
import {
  useArchiveGoalTemplate,
  useCreateGoalTemplate,
  useGoalTemplates,
  useUpdateGoalTemplate,
} from "@/hooks/useGoalTemplates";
import { useRewardsEngine } from "@/hooks/useRewardsEngine";

import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useAppTheme } from "@/utils/theme";
import { toTitleCase } from "@/utils/toTitleCase";

import {
  GoalTemplate,
  GoalTemplatePayload,
} from "@/domain/goalTemplates.interface";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";

import GoalTemplateFilters, {
  Filters,
} from "@/ui/content/goalTemplates/Filters";
import GoalTemplateModal from "@/ui/modals/goalTemplates/GoalTemplateModal";

const DEFAULT_FILTERS: Filters = {
  sortField: "name",
  sortOrder: 1,
  pageNumber: 0,
  pageSize: 10,
  search: "",
  status: "",
  rewardConfigurationId: "",
};

const GoalTemplatesPage: FC = () => {
  const theme = useAppTheme();
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const { setPageTitle } = usePageStore();

  const filtersState = useState<Filters>(DEFAULT_FILTERS);
  const [filters, setFilters] = filtersState;
  const debouncedSearch = useDebounce({ value: filters.search });

  const [modalState, setModalState] = useState<{
    open: boolean;
    goalTemplate?: GoalTemplate;
  }>({ open: false });

  const [deleteAlert, setDeleteAlert] = useState<{
    open: boolean;
    goalTemplate?: GoalTemplate;
  }>({ open: false });

  const {
    goalTemplates,
    isLoadingGoalTemplates,
    isFetchingGoalTemplates,
    refetchGoalTemplates,
  } = useGoalTemplates({
    sortField: filters.sortField,
    sortOrder: filters.sortOrder,
    pageNumber: filters.pageNumber,
    pageSize: filters.pageSize,
    search: debouncedSearch,
    status: filters.status,
    rewardConfigurationId: filters.rewardConfigurationId,
  });

  const createTemplate = useCreateGoalTemplate();
  const updateTemplate = useUpdateGoalTemplate();
  const archiveTemplate = useArchiveGoalTemplate();
  const createAuditLog = useCreateAuditlog();

  const { data: rewardConfigs } = useRewardsEngine(true);

  const rewardConfigNameById = useMemo(() => {
    const map = new Map<string, string>();
    (rewardConfigs || []).forEach((rc) => {
      if (rc._id) {
        const label = rc.event
          ? `${rc.event}${rc.eventType ? ` (${rc.eventType})` : ""}`
          : rc._id;
        map.set(rc._id, label);
      }
    });
    return map;
  }, [rewardConfigs]);

  const isSaving = createTemplate.isPending || updateTemplate.isPending;

  useEffect(() => {
    setPageTitle("Goal Templates");
  }, []);

  useAuditLogFirstRender({
    appType: "WEB_BACK_OFFICE",
    module: MODULE_NAME.GOAL_TEMPLATES,
    option: OPTIONS.GOAL_TEMPLATES_LIST,
    actionCode: ACTION_CODE.WEB_GOAL_TPL_LIST_VIEW,
    action: "When entering",
    detail: "Viewed the list of goal templates",
    transactionDate: new Date(),
    accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
    createdBy: userInfo?._id as string,
    programId: userInfo?.programId as string,
    orgUnitId: userInfo?.orgUnitIds?.[0] as string,
  });

  useEffect(() => {
    refetchGoalTemplates();
  }, [
    debouncedSearch,
    filters.status,
    filters.rewardConfigurationId,
    filters.sortField,
    filters.sortOrder,
    filters.pageNumber,
    filters.pageSize,
  ]);

  useEffect(() => {
    if (debouncedSearch !== "") {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.GOAL_TEMPLATES,
        option: OPTIONS.GOAL_TEMPLATES_LIST,
        actionCode: ACTION_CODE.WEB_GOAL_TPL_LIST_SEARCH,
        action: "When searching",
        detail: "Searched within the list of goal templates",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });
    }
  }, [debouncedSearch]);

  const handleOpenCreate = () => {
    setModalState({ open: true, goalTemplate: undefined });
  };

  const handleOpenEdit = (goalTemplate: GoalTemplate) => {
    setModalState({ open: true, goalTemplate });
  };

  const handleCloseModal = () => {
    setModalState({ open: false, goalTemplate: undefined });
  };

  const handleSave = async (data: GoalTemplatePayload) => {
    const editing = modalState.goalTemplate;

    try {
      if (editing) {
        await updateTemplate.mutateAsync({ id: editing._id, payload: data });

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.GOAL_TEMPLATES,
          option: OPTIONS.GOAL_TEMPLATE_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_GOAL_TPL_EDIT,
          action: "When editing a goal template",
          detail: `Edited goal template ${data.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The goal template has been updated.", "success");
      } else {
        await createTemplate.mutateAsync(data);

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.GOAL_TEMPLATES,
          option: OPTIONS.GOAL_TEMPLATE_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_GOAL_TPL_ADD,
          action: "When creating a goal template",
          detail: `Created goal template ${data.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The goal template has been created.", "success");
      }

      handleCloseModal();
      refetchGoalTemplates();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const handleConfirmDelete = async () => {
    const target = deleteAlert.goalTemplate;
    if (!target) return;

    try {
      await archiveTemplate.mutateAsync(target._id);

      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.GOAL_TEMPLATES,
        option: OPTIONS.DELETE,
        actionCode: ACTION_CODE.WEB_GOAL_TPL_DELETE,
        action: "When deleting a goal template",
        detail: `Deleted goal template ${target.name}`,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });

      showSnackBar("The registry has been deleted.", "success");
      setDeleteAlert({ open: false });
      refetchGoalTemplates();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const headCells: HeadCell[] = [
    {
      id: "name",
      isSortable: true,
      disablePadding: true,
      label: "Name",
      align: "left",
      width: "220px",
    },
    {
      id: "rewardConfig",
      isSortable: false,
      disablePadding: false,
      label: "Reward config",
      align: "left",
      width: "220px",
    },
    {
      id: "steps",
      isSortable: false,
      disablePadding: false,
      label: "Steps",
      align: "left",
      width: "100px",
    },
    {
      id: "status",
      isSortable: true,
      disablePadding: false,
      label: "Status",
      align: "left",
      width: "120px",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: " ",
      align: "right",
      width: "80px",
    },
  ];

  const rows = useMemo(() => {
    if (!goalTemplates || goalTemplates.list.length === 0) return [];

    return goalTemplates.list.map((row, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => handleOpenEdit(row),
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () => setDeleteAlert({ open: true, goalTemplate: row }),
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];

      const rewardConfigLabel =
        rewardConfigNameById.get(row.rewardConfigurationId) ||
        row.rewardConfigurationId ||
        "-";

      return createDataRow(index, {
        name: <div>{row.name}</div>,
        rewardConfig: <div>{rewardConfigLabel}</div>,
        steps: <div>{(row.steps || []).length}</div>,
        status: row.status ? (
          <Chip
            sx={{
              bgcolor:
                row.status === "ACTIVE"
                  ? theme.palette.brand.light
                  : theme.palette.warning.light,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor:
                row.status === "ACTIVE"
                  ? theme.palette.brand.main
                  : theme.palette.warning.dark,
            }}
            label={<div className="text-xs">{toTitleCase(row.status)}</div>}
          />
        ) : (
          <div>-</div>
        ),
        action: <ActionMenu id={row._id} options={actionMenu} />,
      });
    });
  }, [goalTemplates, theme, rewardConfigNameById]);

  return (
    <>
      <div className="w-full overflow-y-auto scroll-smooth">
        <div className="full-width-container mx-auto">
          <div className="flex flex-col items-center justify-start min-h-screen space-y-5">
            <Card sx={{ width: "100%", borderRadius: "8px" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "20px",
                }}
              >
                <Box
                  sx={{ width: "100%" }}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="headlineSmall" component="h1">
                    Goal Templates
                  </Typography>

                  <Button
                    size="medium"
                    onClick={handleOpenCreate}
                    variant="contained"
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      textAlign: "center",
                      height: "40px",
                      backgroundColor: theme.palette.primary.dark,
                      color: theme.palette.textMain.light,
                    }}
                    startIcon={<AddIcon />}
                  >
                    ADD
                  </Button>
                </Box>

                <GoalTemplateFilters filtersState={filtersState} />

                <DataTable
                  data={rows}
                  headCells={headCells}
                  order={filters.sortOrder === 1 ? "asc" : "desc"}
                  orderBy={filters.sortField}
                  rowsPerPage={filters.pageSize}
                  page={filters.pageNumber}
                  total={goalTemplates?.totalCount}
                  isLoading={isLoadingGoalTemplates || isFetchingGoalTemplates}
                  messageEmptyData="No goal templates to show."
                  onChangePage={(_event, newPage) => {
                    setFilters((prev) => ({ ...prev, pageNumber: newPage }));
                  }}
                  onChangeSize={(event) => {
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: 0,
                      pageSize: Number(event.target.value),
                    }));
                  }}
                  onChangeSort={(order, orderBy) => {
                    setFilters((prev) => {
                      const newSortField = orderBy as string;
                      const newSortOrder = order === "asc" ? 1 : -1;
                      if (
                        prev.sortField === newSortField &&
                        prev.sortOrder === newSortOrder
                      ) {
                        return prev;
                      }

                      return {
                        ...prev,
                        pageNumber: 0,
                        sortField: newSortField,
                        sortOrder: newSortOrder,
                      };
                    });
                  }}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {modalState.open && (
        <GoalTemplateModal
          open={modalState.open}
          onClose={handleCloseModal}
          goalTemplate={modalState.goalTemplate}
          onSave={handleSave}
          isPending={isSaving}
        />
      )}

      {deleteAlert.open && (
        <GenericAlertModal
          onCancel={() => setDeleteAlert({ open: false })}
          onClick={handleConfirmDelete}
          title="Delete"
          description="Are you sure you want to delete this registry?"
          submitText="Confirm"
          cancelText="Cancel"
          isLoading={archiveTemplate.isPending}
          theme={theme}
        />
      )}
    </>
  );
};

export default GoalTemplatesPage;
